import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { VENDOR_PAYMENT } from '../../constants/route-paths';
import { format } from 'date-fns';
import { toUSD, numberFormat } from '../../util/currency-formatter';
import { isInlineItem, INLINE_CATALOG_DISPLAY_NAME } from '../../util/common';
import { GSTORVAT } from '../../util/enums';
import { useDispatch } from 'react-redux';
import { getPOInvoice } from '../../actions/project-management.action';
import CustomTable from '../table/table';
import idx from 'idx';
import './view-invoice-modal.scss';

function ViewInvoiceModal({ show, onHide, invoiceData = {} }) {
  const [loading, setLoading] = useState(false);
  const [invoiceDetails, setInvoiceDetails] = useState({});
  const [items, setItems] = useState([]);
  const [vendorType, setVendorType] = useState('');

  const dispatch = useDispatch()

  useEffect(() => {
    if (show && (invoiceData.invoiceId)) {
      fetchInvoiceDetails();
    }
  }, [show, invoiceData.invoiceId]);

  const fetchInvoiceDetails = async (targetId) => {
    setLoading(true);
    try {
      const invoiceId = targetId || invoiceData.invoiceId;
      if (!invoiceId) {
        setInvoiceDetails(invoiceData);
        setLoading(false);
        return;
      }

      let invoice = []
      invoice = await dispatch(getPOInvoice(invoiceId));

      // Format the data similar to view-invoice.jsx
      const formattedData = {
        poId: idx(invoice, (_) => _.purchaseOrder.u_id),
        poNumber: idx(invoice, (_) => _.purchaseOrder.f_id),
        fId: idx(invoice, (_) => _.purchaseOrder.f_id),
        projectId: invoice.projectId || idx(invoice, (_) => _.purchaseOrder.projectId),
        invoiceNo: invoice.invoiceNo,
        amountPaid: invoice.amountPaid,
        retentionAmount: invoice.retentionAmount,
        retentionReleasedAmount: invoice.retentionReleasedAmount,
        invoiceURL: invoice.invoiceURL,
        invoiceClaim: invoice.invoiceClaim,
        invoiceDate: invoice.invoiceDate,
        dueDate: invoice.dueDate,
        approvedAmount: invoice.approvedAmount,
        subTotal: invoice.subTotal,
        taxAmount: invoice.taxAmount,
        progressClaimNumber: invoice.progress_claim_number,
        isCreditNote: Boolean(idx(invoice, _ => _.isCreditNote)),
        referenceInvoiceId: idx(invoice, _ => _.referenceInvoiceId) || null,
        originalInvoiceNo: idx(invoice, _ => _.originalInvoice.invoiceNo) || null,
        creditNotes: idx(invoice, _ => _.creditNotes) || [],
      };

      // Get the vendor type from the response
      const vendorTypeFromResponse = idx(invoice, (_) => _.purchaseOrder.vendor.type);
      setVendorType(vendorTypeFromResponse || 'Supplier');

      // Set items from the response
      setItems(idx(invoice, (_) => _.items) || []);
      setInvoiceDetails(formattedData);
    } catch (error) {
      console.error("Error fetching invoice details:", error);
      // If we can't fetch the details, use what was passed in
      setInvoiceDetails(invoiceData);
      setItems(invoiceData.items || []);
      setVendorType(invoiceData.vendorType || 'Supplier');
    } finally {
      setLoading(false);
    }
  };

  // Use the fetched data or fall back to the passed data
  const data = invoiceDetails || invoiceData;
  const issueDate = data.invoiceDate ? format(new Date(data.invoiceDate), 'dd/MM/yyyy') : '-';
  const dueDate = data.dueDate ? format(new Date(data.dueDate), 'dd/MM/yyyy') : '-';
  const invoiceId = data.invoiceNo || '-';
  const retentionAmount = data.retentionAmount || 0;
  const retentionReleasedAmount = data.retentionReleasedAmount || 0;
  const invoiceURL = data.invoiceURL || data.invoiceUrl || data.invoice_url || '';
  const displayVendorType = vendorType || invoiceData.vendorType || 'Supplier';
  const displayInvoiceClaim = (displayVendorType === 'sub-contractor' && Array.isArray(items) && items.length > 0)
    ? items.reduce((sum, item) => sum + parseFloat(item.claimAmount || 0), 0)
    : (data.invoiceClaim || 0);

  // Define columns based on vendor type
  const getColumns = () => {
    if (displayVendorType === 'Supplier') {
      return [
        { field: 'taskName', title: 'Task', render: (row) => row.taskName || 'N/A' },
        { field: 'category', title: 'Catalog', render: (item) => isInlineItem(item) ? INLINE_CATALOG_DISPLAY_NAME : (item.category || 'N/A') },
        { field: 'item', title: 'Items Description', render: (row) => row.item || row.description || 'N/A' },
        { field: 'manufacturer', title: 'Category', render: (row) => row.manufacturer || 'N/A' },
        { field: 'partNo', title: 'Part No', render: (row) => row.partNo || 'N/A' },
        { field: 'cost', title: 'Unit Cost', class: 'text-right', render: (row) => numberFormat(row.cost || row.price || 0) },
        { field: 'qty', title: 'Ordered Quantity' },
        { field: 'receivedQty', title: 'Received Quantity' },
        { field: 'previousInv', title: 'Previous Inv' },
        { field: 'invoicedQty', title: 'This Inv' },
        { field: 'unit', title: 'UoM', render: (row) => row.unit || 'N/A' },
        { field: 'amountPaid', title: 'Total', class: 'text-right', render: (rowData) => numberFormat(rowData.amountPaid || 0) }
      ];
    } else {
      return [
        { field: 'taskName', title: 'Task', class: 'w-auto' },
        { field: 'name', title: 'Description', class: 'w-auto' },
        { field: 'total', title: 'Total Cost', class: 'w-auto text-right', render: (item) => numberFormat(item.total) },
        { field: 'previousInv', title: 'Prev. Approved', class: 'text-right', render: (item) => numberFormat(item.previousInv) },
        { field: 'claimAmount', title: 'This Claim', class: 'text-right', render: (item) => numberFormat(item.claimAmount) },
        { field: 'approvedAmount', title: 'This Approved', class: 'text-right', render: (item) => numberFormat(item.approvedAmount) }
      ];
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      dialogClassName="invoice-modal-dialog"
      aria-labelledby="invoice-modal"
      centered
      className="invoice-modal"
    >
      <Modal.Header closeButton className='top-0'>
        <Modal.Title id="invoice-modal" className='brand-color-3'>
          {displayVendorType === 'Supplier' ? 'Supplier Invoice Details' : 'Contractor Invoice Details'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="invoice-modal-body">
        {loading ? (
          <div className="text-center p-4">Loading invoice details...</div>
        ) : (
          <div className="row">
            {data.isCreditNote && (
              <div className="col-12 mt-2 mb-2">
                <div className="alert alert-info py-2 px-3 mb-0 d-flex align-items-center" data-cy="credit-note-banner">
                  <span className="badge bg-danger me-2">CREDIT NOTE</span>
                  <span>
                    This is a return stock credit note referencing original invoice:{" "}
                    {data.referenceInvoiceId ? (
                      <>
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={() => fetchInvoiceDetails(data.referenceInvoiceId)}
                          className="fw-bold text-decoration-underline ms-1 text-primary cursor-pointer"
                          title="View original invoice details in modal"
                        >
                          {data.originalInvoiceNo || "Original Invoice"}
                        </span>
                        <Link
                          to={`/${VENDOR_PAYMENT}/view/${data.referenceInvoiceId}?type=project&id=${data.projectId || ''}&poId=${data.poId || ''}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ms-2 text-decoration-none text-primary"
                          title="Open full invoice view in new tab"
                        >
                          <OpenInNewIcon className="open-new-icon" />
                        </Link>
                      </>
                    ) : (
                      <strong>{data.originalInvoiceNo || "Original Invoice"}</strong>
                    )}
                  </span>
                </div>
              </div>
            )}
            {!data.isCreditNote && Array.isArray(data.creditNotes) && data.creditNotes.length > 0 && (
              <div className="col-12 mt-2 mb-2">
                <div className="alert alert-warning py-2 px-3 mb-0 d-flex align-items-center" data-cy="credit-notes-issued-banner">
                  <span className="badge bg-warning text-dark me-2">CREDIT ISSUED</span>
                  <span>
                    Credit Note(s) issued for returned stock against this invoice:{" "}
                    {data.creditNotes.map((cn, idx) => (
                      <span key={cn.id || idx}>
                        {idx > 0 && ", "}
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={() => fetchInvoiceDetails(cn.id)}
                          className="fw-bold text-decoration-underline ms-1 text-primary cursor-pointer"
                          title="View credit note details in modal"
                        >
                          {cn.invoiceNo} ({toUSD(cn.subTotal)})
                        </span>
                        <Link
                          to={`/${VENDOR_PAYMENT}/view/${cn.id}?type=project&id=${data.projectId || ''}&poId=${data.poId || ''}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ms-1 text-decoration-none text-primary"
                          title="Open full credit note view in new tab"
                        >
                          <OpenInNewIcon className="open-new-icon" />
                        </Link>
                      </span>
                    ))}
                  </span>
                </div>
              </div>
            )}
            <div className="col-lg-4 mt-3">
              <div className="view-block">
                <label>{displayVendorType === 'Supplier' ? 'PO' : 'WO'}</label>
                {/* fId is the PO number with project prefix and Suffix if null then default to po Number*/}
                <p>{data.fId || data.poId || data.poNumber || '-'}</p>
              </div>
            </div>
            {/* <div className="col-lg-4 mt-3">
              <div className="view-block">
                <label>Procurement Code</label>
                <p>{data.fId || '-'}</p>
              </div>
            </div> */}
            <div className="col-lg-4 mt-3">
              <div className="view-block">
                <label>Invoice No</label>
                <p>{invoiceId}</p>
              </div>
            </div>
            <div className="col-lg-4 mt-3">
              <div className="view-block">
                <label>Invoice Claim (ex GST)</label>
                <p>{toUSD(displayInvoiceClaim)}</p>
              </div>
            </div>
            <div className="col-lg-4 mt-3">
              <div className="view-block">
                <label>Progress Claim No</label>
                <p>{data.progressClaimNumber || '-'}</p>
              </div>
            </div>

            {displayVendorType === 'Supplier' && (
              <>
                <div className="col-lg-4 mt-3">
                  <div className="view-block">
                    <label>Sub Total (Ex. {GSTORVAT})</label>
                    <p>{toUSD(data.subTotal || 0)}</p>
                  </div>
                </div>
                <div className="col-lg-4 mt-3">
                  <div className="view-block">
                    <label>{GSTORVAT}</label>
                    <p>{toUSD(data.taxAmount || 0)}</p>
                  </div>
                </div>
              </>
            )}

            {displayVendorType === 'sub-contractor' && (
              <div className="col-lg-4 mt-3">
                <div className="view-block">
                  <label>Approved Amount (ex GST)</label>
                  <p>{toUSD(data.approvedAmount || 0)}</p>
                </div>
              </div>
            )}

            <div className="col-lg-4 mt-3">
              <div className="view-block">
                <label>Amount Payable</label>
                <p>{toUSD(data.amountPaid || 0)}</p>
              </div>
            </div>

            {displayVendorType === 'sub-contractor' && (
              <>
                <div className="col-lg-4 mt-3">
                  <div className="view-block">
                    <label>Retention Held (This Claim)</label>
                    <p>{toUSD(retentionAmount)}</p>
                  </div>
                </div>
                <div className="col-lg-4 mt-3">
                  <div className="view-block">
                    <label>Retention Released (This Claim)</label>
                    <p>{toUSD(retentionReleasedAmount)}</p>
                  </div>
                </div>
              </>
            )}

            <div className="col-lg-4 mt-3">
              <div className="view-block">
                <label>Invoice Date</label>
                <p>{issueDate}</p>
              </div>
            </div>

            <div className="col-lg-4 mt-3">
              <div className="view-block">
                <label>Due Date</label>
                <p>{dueDate}</p>
              </div>
            </div>

            {invoiceURL && (
              <div className="col-lg-4 mt-3">
                <div className="view-block text-left">
                  <label>Invoice URL</label>
                  <a href={invoiceURL} rel="noopener noreferrer" target="_blank">
                    Invoice URL
                  </a>
                </div>
              </div>
            )}

            <div className="col-12 mt-4">
              <CustomTable
                columns={getColumns()}
                data={items || []}
                toolbar={false}
                enableSearch={false}
                exportButton={false}
                itemsPerPageCustom={false}
                stickyHeader={true}
              />
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className='bottom-0'>
        <Button className='btn-bg-brand-5 btn-border-none' onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ViewInvoiceModal; 